const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const NAIVE_DATE_TIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?$/;
const EXPLICIT_TIMEZONE_RE = /(Z|[+-]\d{2}:\d{2}|[+-]\d{4})$/i;
const SQLISH_DATETIME_WITH_SPACE_RE = /^(\d{4}-\d{2}-\d{2})\s+/;
const BROWSER_TIME_ZONE =
  typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "";

function safeText(value, fallback = "") {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
}

function normalizeFractionToMilliseconds(value = "") {
  return Number(`${String(value || "").slice(0, 3)}000`.slice(0, 3)) || 0;
}

function withBrowserTimeZone(options = {}) {
  if (!BROWSER_TIME_ZONE || options?.timeZone) {
    return options;
  }

  return {
    ...options,
    timeZone: BROWSER_TIME_ZONE,
  };
}

export function parseDateTimeValue(value) {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value);
  }

  const text = safeText(value);
  if (!text) return new Date(Number.NaN);

  if (/^\d+$/.test(text)) {
    const numeric = Number(text);
    if (Number.isFinite(numeric)) {
      return new Date(text.length <= 10 ? numeric * 1000 : numeric);
    }
  }

  const dateOnlyMatch = text.match(DATE_ONLY_RE);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const naiveDateTimeMatch = text.match(NAIVE_DATE_TIME_RE);
  if (naiveDateTimeMatch && !EXPLICIT_TIMEZONE_RE.test(text)) {
    const [, year, month, day, hour, minute, second = "0", fraction = ""] = naiveDateTimeMatch;
    return new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
        normalizeFractionToMilliseconds(fraction)
      )
    );
  }

  const normalizedText = SQLISH_DATETIME_WITH_SPACE_RE.test(text)
    ? text.replace(SQLISH_DATETIME_WITH_SPACE_RE, "$1T")
    : text;
  const parsed = new Date(normalizedText);
  return Number.isNaN(parsed.getTime()) ? new Date(Number.NaN) : parsed;
}

export function toTimeValue(value) {
  const parsed = parseDateTimeValue(value);
  const timestamp = parsed.getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export function formatLocalDateTime(value, options = {}, fallback = "") {
  const parsed = parseDateTimeValue(value);
  if (Number.isNaN(parsed.getTime())) {
    return fallback || safeText(value);
  }

  return parsed.toLocaleString(undefined, withBrowserTimeZone(options));
}

export function formatLocalDate(value, options = {}, fallback = "") {
  const parsed = parseDateTimeValue(value);
  if (Number.isNaN(parsed.getTime())) {
    return fallback || safeText(value);
  }

  return parsed.toLocaleDateString(undefined, withBrowserTimeZone(options));
}

export function formatLocalTime(value, options = {}, fallback = "") {
  const parsed = parseDateTimeValue(value);
  if (Number.isNaN(parsed.getTime())) {
    return fallback || safeText(value);
  }

  return parsed.toLocaleTimeString(undefined, withBrowserTimeZone(options));
}
